import * as React from 'react';
import * as cx from 'classnames';
import { noDefault } from 'lib/eventWrappers';
import SuperCritical from 'lib/supercritical';

import {
    CARGO_ERROR_MISSING_ACTIVITY_TYPE,
    CARGO_ERROR_MISSING_CARGO_CATEGORY,
    CARGO_ERROR_MISSING_TRADE_ROUTE,
    CARGO_ERROR_MISSING_QUANTITY,
    CARGO_ERROR_QUANTITY_NONPOSITIVE,
    CargoActivityViewModel,
    CRUISE_ERROR_CREW_NEGATIVE,
    CRUISE_ERROR_DISEMBARK_NEGATIVE,
    CRUISE_ERROR_EMBARK_NEGATIVE,
    CRUISE_ERROR_IN_TRANSIT_NEGATIVE,
    CRUISE_ERROR_MISSING_PASSENGER_COUNT,
    CruiseActivityViewModel,
    InPortOps,
} from './model';

import { FootmarkAssignmentsView } from 'portcall/footmarkassignments/view';
import { ListErrors } from 'core/validationerror/view';
import { NullableNumericInput } from 'portcall/numericinput/view';

// InPortOpsProps is a great tongue twister! :D
export interface InPortOpsProps {
    model : InPortOps;
    /** Set to true to include header and border around the In Port Ops UI. */
    includeChrome? : boolean;
    /** Set to true to include instruction text along-side the In Port Ops UI. */
    includeInstructions? : boolean;
    /** Set tab index for multiple inport operations. */
    tabIndex? : number;
    /** Set to true to auto focus the first footmark input. */
    autoFocus? : boolean;
}

export const InPortOpsView = SuperCritical(({ model: m, includeChrome, includeInstructions, tabIndex, autoFocus } : InPortOpsProps) =>
    <div className={cx('inportops', { 'include-instructions': includeInstructions })}>
        {includeChrome && <OpsHeader model={m} />}
        <div className={cx("inportops-main", { 'include-chrome': includeChrome })}>
            <CargoActivities model={m} includeInstructions={includeInstructions} />
            <CruiseActivities model={m} />
            <PierPosition model={m} includeInstructions={includeInstructions} tabIndex={tabIndex} autoFocus={autoFocus} />
        </div>
    </div>
);

const EM_DASH = '\u2014';
const OpsHeader = SuperCritical(({ model: m } : { model : InPortOps }) => {
    const l = m.Reservation.Itinerary()?.Location();
    return (
        <div className="inportops-header">
            {/* For now, not including the port/region name on the thinking that in all spots where we're
                planning to use this header UI, we're also planning to include the <PortCallScheduleHeaderView />,
                which already lists the port. */}
            In Port Ops{l && ` ${EM_DASH} ${l.Name()}`}
        </div>
    );
});

const CargoActivities = SuperCritical(({ model: m, includeInstructions } : {
    model : InPortOps,
    includeInstructions : boolean | undefined
}) =>
    <div className="ops-section in-port-activities-section cargo-activities-section">
        <div className="ops-section-main">
            <div className="ops-section-header">
                <div className="ops-section-title">Cargo Activities</div>
                {m.Reservation.HasLayOpsOnly() &&
                    <div className="lay-only-label">Lay Only</div>
                }
            </div>
            <div className="ops-section-body in-port-activities-body cargo-activities-body">
                {m.CargoActivities().length > 0 &&
                    <div
                        className={cx('column-headers cargo-columns-shell', {
                            'has-trade-route': m.ShouldShowTradeRoute()
                        })}
                    >
                        <span className="col-header col-trade-route">Trade Route</span>
                        <span className="col-header col-activity-type">Activity</span>
                        <span className="col-header col-cargo-category">Cargo</span>
                        <span className="col-header col-quantity">Qty</span>
                        <span className="col-header col-delete" />
                    </div>
                }
                {m.CargoActivities().map(a =>
                    <CargoActivityRow activity={a} model={m} key={CargoRowKey(a)} />
                )}
                {m.CanEdit() &&
                    <div className="add-activity-shell">
                        <span className="add-cargo-activity-shell">
                            {m.HasRequiredCargoActivityLookups() &&
                                <a href="" className="add-activity" onClick={noDefault(m.AddNewCargoActivity)}>
                                    + Add Cargo Activity
                                </a>
                            }
                        </span>
                        <LayOnlySelector model={m} />
                    </div>
                }
            </div>
        </div>
        {includeInstructions && <ActivitiesInstructions model={m} />}
    </div>
);

let nextCargoRowKey = 0;
const CARGO_ROW_KEYS = new WeakMap<CargoActivityViewModel, number>();
function CargoRowKey(a : CargoActivityViewModel) {
    let id = CARGO_ROW_KEYS.get(a);
    if (id === undefined) {
        id = nextCargoRowKey++;
        CARGO_ROW_KEYS.set(a, id);
    }
    return id;
}

const CargoActivityRow = SuperCritical(({ activity: a, model: m } : { activity : CargoActivityViewModel, model : InPortOps }) => {
    const disabled= !m.CanEdit() || a.IsEditingLocked();
    return (
        <div className="in-port-activity cargo-activity">
            <div
                className={cx('activity-data cargo-columns-shell', {
                    'has-trade-route': m.ShouldShowTradeRoute()
                })}
            >
                <TradeRouteColumn    activity={a} disabled={disabled} />
                <ActivityTypeColumn  activity={a} disabled={disabled} />
                <CargoCategoryColumn activity={a} disabled={disabled} />
                <span className="col-quantity">
                    <NullableNumericInput
                        className="quantity-num activity-data-entry"
                        value={a.Model.Quantity.Num}
                        disabled={disabled}
                    />
                    {' '}
                    <span className="quantity-uom">{a.Model.Quantity.UOM()}</span>
                </span>
                <span className="col-delete">
                    {m.CanEdit() &&
                        <a
                            href=""
                            className="fa fa-times-circle delete-btn"
                            onClick={noDefault(a.Delete)}
                        />
                    }
                </span>
            </div>
            {
                m.CanEdit() && a.IsEditingLocked() ?
                    <div className="unlock-activity">
                        There are errors with this activity.
                        {' '}
                        <a href="" onClick={noDefault(a.UnlockEditing)}>Unlock to fix.</a>
                    </div> :
                !a.IsEditingLocked() ?
                    <CargoValidationMessage activity={a} /> :
                null
            }
        </div>
    );
});

const TradeRouteColumn = SuperCritical(({ activity: a, disabled } : { activity : CargoActivityViewModel, disabled : boolean }) =>
    <span className="col-trade-route">
        <select
            className="pc-select activity-data-entry"
            value={a.Model.TradeRouteId() ?? ''}
            onChange={e => e.currentTarget.value && a.Model.TradeRouteId(+e.currentTarget.value)}
            disabled={disabled}
        >
            {a.Model.TradeRouteId() === null && <option value="">--</option>}
            {a.AvailableTradeRoutes().map(tr =>
                <option value={tr.Id()} key={tr.Id()}>{tr.Name()}</option>
            )}
        </select>
        <span className="pc-select-chevron" />
    </span>
);

const ActivityTypeColumn = SuperCritical(({ activity: a, disabled } : { activity : CargoActivityViewModel, disabled : boolean }) =>
    <span className="col-activity-type">
        <select
            className="pc-select activity-data-entry"
            value={a.Model.ActivityTypeId() ?? ''}
            onChange={e => e.currentTarget.value && a.Model.ActivityTypeId(+e.currentTarget.value)}
            disabled={disabled}
        >
            {a.Model.ActivityTypeId() === null && <option value="">--</option>}
            {a.AvailableActivityTypes().map(t =>
                <option value={t.Id()} key={t.Id()}>{t.Name()}</option>
            )}
        </select>
        <span className="pc-select-chevron" />
    </span>
);

const CargoCategoryColumn = SuperCritical(({ activity: a, disabled } : { activity : CargoActivityViewModel, disabled : boolean }) =>
    <span className="col-cargo-category">
        <select
            className="pc-select activity-data-entry"
            value={a.Model.CargoCategoryId() ?? ''}
            onChange={e => e.currentTarget.value && a.Model.CargoCategoryId(+e.currentTarget.value)}
            disabled={disabled}
        >
            {a.Model.CargoCategoryId() === null && <option value="">--</option>}
            {a.AvailableCargoCategories().map(c =>
                <option value={c.Id()} key={c.Id()}>{c.Name()}</option>
            )}
        </select>
        <span className="pc-select-chevron" />
    </span>
);

const CargoValidationMessage = SuperCritical(({ activity: a } : { activity : CargoActivityViewModel }) => {
    const errors = a.Errors();
    return errors === 0 ?
        null :
        <div className="activity-validation">
            {
                // Missing required fields: We figure these are inevitable for just-added activities, so
                // we'll only list one at a time and we'll style them more as instructions than as errors.
                (errors & CARGO_ERROR_MISSING_TRADE_ROUTE) !== 0 ?
                    <div className="required-field">Enter Trade Route</div> :
                (errors & CARGO_ERROR_MISSING_ACTIVITY_TYPE) !== 0 ?
                    <div className="required-field">Enter Activity</div> :
                (errors & CARGO_ERROR_MISSING_CARGO_CATEGORY) !== 0 ?
                    <div className="required-field">Enter Cargo</div> :
                (errors & CARGO_ERROR_MISSING_QUANTITY) !== 0 ?
                    <div className="required-field">Enter Quantity</div> :
                null
            }
            {/* All other errors: List each error and style as an error. We expect these to be rare. */}
            {(errors & CARGO_ERROR_QUANTITY_NONPOSITIVE) !== 0 &&
                <div className="alert alert-danger">Quantity must be positive.</div>
            }
        </div>;
});

const LayOnlySelector = SuperCritical(({ model : m } : { model : InPortOps }) => {
    // Only render a Lay Only selector if we have no cargo or cruise activities AND we haven't already
    // persisted some port ops data.
    const shouldRender =
        m.CargoActivities ().length === 0
     && m.CruiseActivities().length === 0
     && !m.HasAnyPortOpsPersisted();
    return shouldRender ?
        <label className="lay-only-selector-shell">
            <input
                type="checkbox"
                className="pc-breaker-input"
                checked={m.Reservation.HasLayOpsOnly()}
                onChange={e => m.Reservation.HasLayOpsOnly(e.currentTarget.checked)}
                tabIndex={400}
            />
            <span className="pc-breaker-light lay-only-selector" />
            {' '}
            Lay Only
        </label> :
        null;
});

const ActivitiesInstructions = SuperCritical(({ model: m } : { model : InPortOps }) =>
    <div className="ops-section-instructions">
        {
            m.AreActivitiesRequired() ?
                'At least one row of Cargo or Cruise Activities required.' :
            m.HasRequiredCargoActivityLookups() ?
                'Enter Cargo or Cruise Activities.' :
            'Enter Cruise Activities.'
        }
        {' '}
        If none, select Lay Only.
    </div>
);

const CruiseActivities = SuperCritical(({ model: m } : { model : InPortOps }) =>
    <div className="ops-section in-port-activities-section cruise-activities-section">
        <div className="ops-section-main">
            <div className="ops-section-header ops-section-title">Cruise Activities</div>
            <div className="ops-section-body in-port-activities-body cruise-activities-body">
                {m.CruiseActivities().length > 0 &&
                    <div className="default-guest-count-shell">
                        Default Guest Count &mdash;
                        {' '}
                        {m.Reservation.Itinerary()?.PortCall()?.Vessel()?.ExpectedPassengerCount() ?? 'N/A'}
                    </div>
                }
                {m.CruiseActivities().length > 0 &&
                    <div className="column-headers cruise-columns-shell">
                        <span className="col-header col-disembark">Disembark</span>
                        <span className="col-header col-embark">Embark</span>
                        <span className="col-header col-in-transit">In Transit</span>
                        <span className="cruise-field-separator" />
                        <span className="col-header col-crew">Crew</span>
                        <span className="col-header col-voyage">Voyage</span>
                        <span className="col-header col-delete" />
                    </div>
                }
                {m.CruiseActivities().map(a =>
                    <CruiseActivityRow activity={a} model={m} key={CruiseRowKey(a)} />
                )}
                {m.CanEdit() &&
                    <div className="add-activity-shell">
                        <a href="" className="add-activity" onClick={noDefault(m.AddNewCruiseActivity)}>
                            + Add Passengers
                        </a>
                    </div>
                }
            </div>
            {m.ActivitiesSaveHandler && ListErrors(m.ActivitiesSaveHandler.Validation())}
            <ActivitiesSaveControls model={m} />
        </div>
    </div>
);

let nextCruiseRowKey = 0;
const CRUISE_ROW_KEYS = new WeakMap<CruiseActivityViewModel, number>();
function CruiseRowKey(a : CruiseActivityViewModel) {
    let id = CRUISE_ROW_KEYS.get(a);
    if (id === undefined) {
        id = nextCruiseRowKey++;
        CRUISE_ROW_KEYS.set(a, id);
    }
    return id;
}

const CruiseActivityRow = SuperCritical(({ activity: a, model: m } : { activity : CruiseActivityViewModel, model: InPortOps }) => {
    const disabled = !m.CanEdit() || a.IsEditingLocked();
    return (
        <div className="in-port-activity cruise-activity">
            <div className="activity-data cruise-columns-shell">
                <span className="col-disembark">
                    <NullableNumericInput
                        className="activity-data-entry"
                        value={a.Model.Disembark}
                        disabled={disabled}
                    />
                </span>
                <span className="col-embark">
                    <NullableNumericInput
                        className="activity-data-entry"
                        value={a.Model.Embark}
                        disabled={disabled}
                    />
                </span>
                <span className="col-in-transit">
                    <NullableNumericInput
                        className="activity-data-entry"
                        value={a.Model.InTransit}
                        disabled={disabled}
                    />
                </span>
                <span className="cruise-field-separator" />
                <span className="col-crew">
                    <NullableNumericInput
                        className="activity-data-entry"
                        value={a.Model.Crew}
                        disabled={disabled}
                        placeholder="optional"
                    />
                </span>
                <span className="col-voyage">
                    <input
                        className="activity-data-entry"
                        value={a.Model.VoyageNumber() ?? ''}
                        onChange={e => a.Model.VoyageNumber(e.currentTarget.value || null)}
                        disabled={disabled}
                        placeholder="optional"
                    />
                </span>
                <span className="col-delete">
                    {m.CanEdit() &&
                        <a
                            href=""
                            className="fa fa-times-circle delete-btn"
                            onClick={noDefault(a.Delete)}
                        />
                    }
                </span>
            </div>
            {
                m.CanEdit() && a.IsEditingLocked() ?
                    <div className="unlock-activity">
                        There are errors with this activity.
                        {' '}
                        <a href="" onClick={noDefault(a.UnlockEditing)}>Unlock to fix.</a>
                    </div> :
                !a.IsEditingLocked() ?
                    <CruiseValidationMessage activity={a} /> :
                null
            }
        </div>
    );
});

const CruiseValidationMessage = SuperCritical(({ activity: a } : { activity : CruiseActivityViewModel }) => {
    const errors = a.Errors();
    return errors === 0 ?
        null :
        <div className="activity-validation">
            {
                // Missing required fields: We figure these are inevitable for just-added activities, so
                // we'll only list one at a time and we'll style them more as instructions than as errors.
                (errors & CRUISE_ERROR_MISSING_PASSENGER_COUNT) !== 0 ?
                    <div className="required-field">Enter Disembark, Embark, or In Transit.</div> :
                null
            }
            {/* All other errors: List each error and style as an error. We expect these to be rare. */}
            {(errors & CRUISE_ERROR_DISEMBARK_NEGATIVE) !== 0 &&
                <div className="alert alert-danger">Disembark can't be negative.</div>
            }
            {(errors & CRUISE_ERROR_EMBARK_NEGATIVE) !== 0 &&
                <div className="alert alert-danger">Embark can't be negative.</div>
            }
            {(errors & CRUISE_ERROR_IN_TRANSIT_NEGATIVE) !== 0 &&
                <div className="alert alert-danger">In Transit can't be negative.</div>
            }
            {(errors & CRUISE_ERROR_CREW_NEGATIVE) !== 0 &&
                <div className="alert alert-danger">Crew can't be negative.</div>
            }
        </div>
});

const ActivitiesSaveControls = SuperCritical(({ model: m } : { model : InPortOps }) =>
    m.ActivitiesSaveHandler?.HasUnsavedChanges() ?
        <div className="ops-section-save-controls">
            {!m.ActivitiesSaveHandler.IsSaving() &&
                <a
                    href=""
                    className="cancel-btn"
                    onClick={noDefault(m.ActivitiesSaveHandler.QuitChanges)}
                >
                    Cancel
                </a>
            }
            <button
                className="pc-button"
                onClick={m.ActivitiesSaveHandler.PerformSave}
                disabled={!m.AreActivitiesValidForSave() || m.ActivitiesSaveHandler.IsSaving()}
            >
                Save {m.Reservation.HasLayOpsOnly() ? 'Lay Only' : 'Activities'}
            </button>
        </div> :
        null
);

const PierPosition = SuperCritical(({ model: m, includeInstructions, tabIndex, autoFocus } : {
    model : InPortOps,
    includeInstructions : boolean | undefined,
    tabIndex : number | undefined,
    autoFocus : boolean | undefined
}) =>
    <div className="ops-section pier-position-section">
        <div className="ops-section-main">
            <div className="ops-section-header ops-section-title">
                Footmarks and Side-To
            </div>
            <div className="ops-section-body">
                <FootmarkAssignmentsView 
                    model={m.FootmarkAssignments} 
                    autoFocus={autoFocus} 
                    tabIndex={{
                        startmark: tabIndex ? tabIndex + 1 : 1,
                        endmark: tabIndex ? tabIndex + 2 : 2,
                        sideto: tabIndex ? tabIndex + 3 : 3,
                    }} 
                />
            </div>
            {m.PierPositionSaveHandler && ListErrors(m.PierPositionSaveHandler.Validation())}
            <PierPositionSaveControls model={m} />
        </div>
        {includeInstructions &&
            <div className="ops-section-instructions">Indicate the ship's Footmarks and select a Side-To</div>
        }
    </div>
);

const PierPositionSaveControls = SuperCritical(({ model: m } : { model : InPortOps }) =>
    m.PierPositionSaveHandler?.HasUnsavedChanges() ?
        <div className="ops-section-save-controls">
            {!m.PierPositionSaveHandler.IsSaving() &&
                <a
                    href=""
                    className="cancel-btn"
                    onClick={noDefault(m.PierPositionSaveHandler.QuitChanges)}
                >
                    Cancel
                </a>
            }
            <button
                className="pc-button"
                onClick={m.PierPositionSaveHandler.PerformSave}
                disabled={m.PierPositionSaveHandler.IsSaving()}
            >
                Save Footmarks &amp; Side-To
            </button>
        </div> :
        null
);



// WEBPACK FOOTER //
// ./apps/portcall/inportops/view.tsx